/**
 * SKU AI配置管理面板 - 主组件
 * 职责: 组装AI配置管理的各个子组件
 * 限制: <150行
 */

import React, { useEffect } from 'react';
import { Card, Select, Button, Form, Modal, Tooltip, Tag, Space } from 'antd';
import { SettingOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { UserPreference } from '../../../types/ai-analysis-types';
import { useAIConfig } from '../../../contexts/AIConfigContext';
import { useAIConfigPanel } from './useAIConfigPanel';
import { AIConfigForm } from './AIConfigForm';
import '../AIConfigPanel.css';

interface AIConfigPanelProps {
  skuId: number;
  skuName: string;
  onConfigChange?: (config: UserPreference | null) => void;
}

export const AIConfigPanel: React.FC<AIConfigPanelProps> = ({ skuId, skuName, onConfigChange }) => {
  const { currentConfig, setCurrentConfig } = useAIConfig();
  const [form] = Form.useForm();
  const {
    configs,
    loading,
    modalVisible,
    editingConfig,
    setModalVisible,
    loadConfigs,
    handleSave,
    handleDelete,
    handleSetDefault,
    handleClone,
    handleCreate,
    handleEdit,
  } = useAIConfigPanel({ skuId, onConfigChange, setCurrentConfig });

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // 表单提交
  const onModalOk = async () => {
    try {
      const values = await form.validateFields();
      await handleSave(values);
      form.resetFields();
    } catch (error) {
      // 验证失败
    }
  };

  // 打开编辑时设置表单值
  useEffect(() => {
    if (modalVisible && editingConfig) {
      form.setFieldsValue(editingConfig);
    } else if (modalVisible) {
      form.resetFields();
      form.setFieldsValue({
        config_name: '新配置',
        main_goal: 'beginner',
        budget_range: 'under_5k',
        experience_level: 'beginner',
        time_expectation: 'quick_return',
        custom_requirements: '',
      });
    }
  }, [modalVisible, editingConfig, form]);

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          <span>智能分析配置</span>
          <Tooltip title="为此SKU创建专属的AI分析配置">
            <Tag color="blue">{skuName}</Tag>
          </Tooltip>
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="small"
          onClick={handleCreate}
        >
          新建配置
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ whiteSpace: 'nowrap' }}>当前配置:</span>
        <Select
          style={{ flex: 1 }}
          value={currentConfig?.id}
          onChange={(id) => {
            const config = configs.find(c => c.id === id) || null;
            setCurrentConfig(config);
            onConfigChange?.(config);
          }}
          loading={loading}
          placeholder="选择配置"
        >
          {configs.map(config => (
            <Select.Option key={config.id} value={config.id}>
              {config.config_name}
              {config.is_default && <Tag color="green" style={{ marginLeft: 8 }}>默认</Tag>}
            </Select.Option>
          ))}
        </Select>
        {currentConfig && (
          <Space>
            <Tooltip title="编辑"><Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(currentConfig)} /></Tooltip>
            <Tooltip title="复制"><Button icon={<CopyOutlined />} size="small" onClick={() => handleClone(currentConfig.id!)} /></Tooltip>
            {!currentConfig.is_default && <Tooltip title="设为默认"><Button icon={<CheckOutlined />} size="small" onClick={() => handleSetDefault(currentConfig.id!)} /></Tooltip>}
            {!currentConfig.is_default && <Tooltip title="删除"><Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(currentConfig.id!)} /></Tooltip>}
          </Space>
        )}
      </div>

      <Modal
        title={editingConfig ? '编辑配置' : '新建配置'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={onModalOk}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <AIConfigForm />
        </Form>
      </Modal>
    </Card>
  );
};

